
import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/tables/Table";
import { UrgentBooking, UserRole } from '@/types';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { Loader2, Search } from 'lucide-react';
import { useUrgentBookings } from '@/hooks/data/useUrgentBookings';
import { DataTablePagination } from './DataTablePagination';

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
    <button
        onClick={onClick}
        className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>{children}</button>
);

interface UrgentBookingsRealDataTableProps {
  currentUserRole: UserRole;
  currentUserId: string;
}

const UrgentBookingsRealDataTable: React.FC<UrgentBookingsRealDataTableProps> = ({ currentUserRole, currentUserId }) => {
  const [useMockData, setUseMockData] = useState(false);
  const isAdmin = currentUserRole === UserRole.ADMIN || currentUserRole === UserRole.SYSTEM_ADMIN_LIVE;
  const { bookings, isLoading, error, refetch, deleteBooking } = useUrgentBookings(isAdmin, currentUserId, useMockData);
  
  const [filter, setFilter] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const { addLog } = useLogger();

  const handleDelete = async (id: string) => {
    if (!isAdmin) return;
    if (!window.confirm('Are you sure you want to delete this booking?')) return;

    addLog(`Admin ${currentUserId} initiated deletion of booking ${id}`, LogLevel.INFO, LogCategory.DATA);
    await deleteBooking(id);
    refetch();
  };

  const filteredBookings = useMemo(() => bookings.filter((booking: UrgentBooking) =>
    Object.values(booking).some((value) =>
      String(value).toLowerCase().includes(filter.toLowerCase())
    )
  ), [bookings, filter]);

  const pageCount = Math.ceil(filteredBookings.length / pageSize);
  
  const paginatedBookings = useMemo(() => filteredBookings.slice(
    pageIndex * pageSize,
    (pageIndex + 1) * pageSize
  ), [filteredBookings, pageIndex, pageSize]);

  const downloadAsCSV = () => {
    if (paginatedBookings.length === 0) return;
    const headers = Object.keys(paginatedBookings[0]);
    const csvContent = [
      headers.join(','),
      ...paginatedBookings.map(row =>
        headers.map(header =>
          `"${String((row as Record<string, unknown>)[header]).replace(/"/g, '""')}"`
        ).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'urgent-bookings.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
            <input
                type="text"
                placeholder="Filter bookings..."
                value={filter}
                onChange={(e) => {
                    setFilter(e.target.value)
                    setPageIndex(0)
                }}
                className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-64"
            />
        </div>
        <div>
          <button
            onClick={downloadAsCSV}
            className="bg-green-500/20 text-green-300 hover:bg-green-500/40 text-xs font-semibold px-2.5 py-1 rounded-md transition-colors mr-2"
          >
            Download Data
          </button>
          <button
            onClick={() => setUseMockData(!useMockData)}
            className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/40 text-xs font-semibold px-2.5 py-1 rounded-md transition-colors"
          >
            {useMockData ? 'Use Real Data' : 'Use Mock Data'}
          </button>
        </div>
      </div>
      {isLoading ? (
        <div className="flex justify-center items-center h-48">
            <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
            <p className="ml-4 text-kala-400">Loading urgent bookings...</p>
        </div>
      ) : error ? (
        <div className="p-4 mb-4 bg-yellow-500/10 text-yellow-400 text-center text-sm rounded-lg">Warning: {error}</div>
      ) : (
        <>
            <Table>
                <TableCaption>A list of urgent bookings.</TableCaption>
                <TableHeader>
                    <TableRow>
                    <TableHead>Artist Name</TableHead>
                    <TableHead>Event Type</TableHead>
                    <TableHead>Event Date</TableHead>
                    <TableHead>Venue</TableHead>
                    <TableHead>Budget</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {paginatedBookings.map((booking) => (
                    <TableRow key={booking.id}>
                        <TableCell>{booking.artistName}</TableCell>
                        <TableCell>{booking.eventType}</TableCell>
                        <TableCell>{format(new Date(booking.eventDate), 'PPP')}</TableCell>
                        <TableCell>{booking.venue}</TableCell>
                        <TableCell>${booking.budget}</TableCell>
                        {isAdmin && (
                        <TableCell className="text-right">
                            <ActionButton onClick={() => handleDelete(booking.id)} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Delete</ActionButton>
                        </TableCell>
                        )}
                    </TableRow>
                    ))}
                </TableBody>
            </Table>
            <DataTablePagination 
                pageIndex={pageIndex}
                pageCount={pageCount}
                pageSize={pageSize}
                setPageIndex={setPageIndex}
                setPageSize={setPageSize}
            />
        </>
      )}
    </div>
  );
};

export default UrgentBookingsRealDataTable;
